import React, {
  useState,
  forwardRef,
  useImperativeHandle,
  useEffect,
} from 'react';

import dynamic from 'next/dynamic';
import styled from 'styled-components';

const Chart = dynamic(() => import('react-apexcharts'), { ssr: false });

interface WebhookGraphProps {
  color: string;
  x_ordinates: any[];
  y_ordinates: any[];
  timeframe: string;
  type: string;
  count: number;
  text: string;
  Icon: any;
}

const WebhookGraphWrapper = styled.div`
  border: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  box-shadow: 0px 14px 29px rgba(0, 27, 31, 0.04);
  border-radius: 12px;
  background-color: ${(props) => props.theme.colors.background};
  & > .webhooks-graph {
    height: 190px;
  }
`;

const WebhookGraphHeader = styled.div<{
  color: string;
}>`
  display: grid;
  grid-template-columns: 49px 1fr;
  padding: 22px 0px;
  margin: 0 22px;
  border-bottom: 1px solid ${(props) => props.theme.colors.borderColors[1]};
  & > .icon-container {
    height: 49px;
    width: 49px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 10px;
    background-color: ${(props) => props.color};
  }
  & > .stats-container {
    padding: 0 11px;
    display: flex;
    flex-direction: column;
    justify-content: center;

    & > .stats-count {
      font-weight: 700;
      font-size: 24px;
      line-height: 28px;
      color: ${(props) => props.theme.colors.text[1]};
    }
    & > .stats-text {
      font-weight: 600;
      font-size: 14px;
      line-height: 12px;
      color: ${(props) => props.color};
    }
  }
`;

const WebhookGraph = forwardRef((props: WebhookGraphProps, ref) => {
  const zoomedFrames = ['24 Hours', '48 Hours'];

  const [countState, setCountState] = useState(props.count);

  const [options, setOptions] = useState({
    dataLabels: {
      enabled: false,
    },
    tooltip: {
      custom: function ({ series, seriesIndex, dataPointIndex, w }) {
        return (
          '<div class="webhook-graph-tooltip">' +
          '<span>' +
          w.config.chart.id +
          '<b>' +
          series[seriesIndex][dataPointIndex] +
          '</b>' +
          '</span>' +
          '</div>'
        );
      },
      x: {
        show: false,
      },
    },
    stroke: {
      show: true,
      colors: [props.color],
      width: 2,
    },
    fill: {
      colors: [props.color],
    },
    markers: {
      colors: [props.color],
    },
    grid: {
      xaxis: {
        lines: {
          show: false,
        },
      },
      yaxis: {
        show: false,
        lines: {
          show: false,
        },
      },
    },
    yaxis: {
      labels: {
        show: true,
        style: {
          colors: '#9DA3B7 ',
        },
      },
      axisTicks: {
        show: false,
        color: '#525252',
      },
      axisBorder: {
        show: false,
        color: '#525252',
      },
    },
    xaxis: {
      tickPlacement: 'on',
      lines: {
        show: false,
      },
      labels: {
        show: true,
        style: {
          colors: '#9DA3B7',
          cssClass: 'apexcharts-xaxis-label',
        },
      },
      axisTicks: {
        show: false,
        color: '#525252',
      },
      axisBorder: {
        color: '#E5E8F0',
        show: true,
      },
      categories: props.x_ordinates.map(function (date: any) {
        return zoomedFrames.includes(props.timeframe)
          ? date.format('HH:ss')
          : date.format('MM/DD');
      }),
    },
    chart: {
      toolbar: {
        show: false,
      },
      id: props.type,
    },
  });

  const [series, setSeries] = useState([
    {
      name: props.type,
      data: props.y_ordinates,
    },
  ]);

  useEffect(() => {
    setCountState(props.count);
    setSeries([
      {
        name: props.type,
        data: props.y_ordinates,
      },
    ]);
  }, [props.y_ordinates, props.count]);

  useImperativeHandle(ref, () => ({
    refreshData(xArr, yArr) {
      setOptions({
        ...options,
        xaxis: {
          ...options.xaxis,
          categories: xArr.map(function (date) {
            return zoomedFrames.includes(props.timeframe)
              ? date.format('HH:ss')
              : date.format('MM/DD');
          }),
        },
      });

      setSeries([
        {
          name: props.type,
          data: yArr,
        },
      ]);
    },
  }));

  return (
    <WebhookGraphWrapper>
      <WebhookGraphHeader color={props.color}>
        <div className="icon-container">
          <props.Icon />
        </div>
        <div className="stats-container">
          <p className="stats-count">{countState}</p>
          <p className="stats-text">{props.text}</p>
        </div>
      </WebhookGraphHeader>
      <div className="webhooks-graph">
        <Chart
          options={options}
          series={series}
          type="area"
          width="100%"
          height="100%"
        />
      </div>
    </WebhookGraphWrapper>
  );
});

export default WebhookGraph;
